import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { SignIn } from './routes/SignIn';
import { useTranslation } from "react-i18next";

// Split, in both directions. Somebody here to chat should not download
// Stripe's wrapper, and somebody sent here to top up on a weak connection
// should not download the chat surface. One extra round trip buys a much
// smaller first payload on the page where that matters most.
const Feed = lazy(() => import('./routes/Feed').then((m) => ({ default: m.Feed })));
const LiveRoom = lazy(() => import('./routes/LiveRoom').then((m) => ({ default: m.LiveRoom })));
const FeedWatch = lazy(() =>
  import('./routes/FeedWatch').then((m) => ({ default: m.FeedWatch })),
);
const Me = lazy(() => import('./routes/Me').then((m) => ({ default: m.Me })));
const Profile = lazy(() => import('./routes/Profile').then((m) => ({ default: m.Profile })));
const Wallet = lazy(() => import('./routes/Wallet').then((m) => ({ default: m.Wallet })));
const Earnings = lazy(() =>
  import('./routes/Earnings').then((m) => ({ default: m.Earnings })),
);
const Checkout = lazy(() =>
  import('./routes/Checkout').then((m) => ({ default: m.Checkout })),
);
// Lazy too: with the web's chat off (lib/features.ts) nothing loads it.
const Chats = lazy(() => import('./routes/Chats').then((m) => ({ default: m.Chats })));
import { clearSession, getSession, onSessionChange, type Session } from './lib/session';
import { retireWebChatKeys, unlinkThisBrowser } from './lib/device';
import { WEB_CHAT } from './lib/features';
import { SignOutContext } from './components/AppShell';
import { logoutFromServer } from './lib/auth';
import { refreshIfDue } from './lib/refreshSession';
import { ProfileSetup } from './components/ProfileSetup';
import { initCrypto, resetCrypto } from './crypto';
import { installHistoryPaging } from './lib/historyPaging';
import { coinShopUrl } from './lib/coinShop';
import { cardPurchaseFrom, signInReturningTo } from './lib/returnTo';

export function App() {
  // Read once on mount rather than on every render: localStorage is
  // synchronous but it is still a parse per call, and the value only changes
  // through the two callbacks below.
  const [session, setSessionState] = useState<Session | null>(() => getSession());

  const onSignedIn = useCallback((s: Session) => setSessionState(s), []);
  const onSignedOut = useCallback(() => {
    // Unlink server-side FIRST, while the token still works: this is what
    // makes the phone's Linked devices list drop this browser and stops it
    // receiving future room keys. Then end the session itself - the logout
    // call is authenticated by the very token that is about to be thrown
    // away, so it has to go before the clear. Then clear the local session
    // and keys. A browser that is offline still signs out - both calls
    // swallow failure.
    //
    // Everything that touches storage is guarded by the token that was
    // signed out: the screen flips at once, so somebody can sign straight
    // back in while these are still in flight, and a late clear (or a late
    // logout) must not take the new session with it.
    const leaving = getSession()?.token;
    void (async () => {
      if (getSession()?.token !== leaving) return;
      await unlinkThisBrowser();
      await logoutFromServer(undefined, leaving);
    })().finally(() => {
      if (getSession()?.token !== leaving) return;
      clearSession();
      // The keys go with the session. A signed-out browser holding room keys
      // could still decrypt a cached transcript, which is not what signing
      // out means to anyone.
      void resetCrypto();
    });
    setSessionState(null);
  }, []);

  // The server has said this session is over (a refresh answered 401), so
  // there is nobody to tell: no unlink, no logout, just the local clean-up.
  const onSessionDead = useCallback(() => {
    clearSession();
    void resetCrypto();
    setSessionState(null);
  }, []);

  // The profile step ended with a refused date of birth: the account is not
  // to be used. Drop the half-made session, but leave the screen where it is -
  // the notice has to stay up, so this does NOT flip the app to signed out.
  const onProfileRefused = useCallback(async () => {
    const leaving = getSession()?.token;
    await logoutFromServer(undefined, leaving);
    if (getSession()?.token === leaving) {
      clearSession();
      void resetCrypto();
    }
  }, []);

  // Every tab shares one stored session (lib/session.ts): another tab
  // signing in, refreshing the token or signing out reaches this one.
  useEffect(
    () =>
      onSessionChange((next) => {
        setSessionState(next);
        if (!next) void resetCrypto();
      }),
    [],
  );

  // Keep the token alive: once at start and whenever the tab comes back, a
  // new one if this has passed half its life. Free on an ordinary day - the
  // check is a decode and a comparison - and never throws.
  const signedIn = session !== null;
  useEffect(() => {
    if (!signedIn) return;
    let stopped = false;
    const check = () => {
      void refreshIfDue().then((outcome) => {
        if (stopped) return;
        if (outcome === 'refreshed') setSessionState(getSession());
        else if (outcome === 'dead') onSessionDead();
      });
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    check();
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [signedIn, onSessionDead]);

  // Bring the crypto up as soon as there is a session to publish keys under.
  // Deliberately not awaited by the render path: a browser that cannot reach
  // the key directory should still show its conversation list, with the
  // messages it cannot read marked as such.
  //
  // NOT on the wallet routes. `initCrypto()` fetches an 843 kB WebAssembly
  // core, and the wallet pages are where somebody lands after being sent to
  // top up - often on a mid-range phone over a weak connection, with no
  // interest in reading a message. Nothing on those pages is encrypted:
  // balances, prices and payouts are all server-side plaintext. Paying for
  // the crypto stack there would be the most expensive download in the
  // product bought for nothing.
  const { pathname } = useLocation();
  const needsCrypto =
    WEB_CHAT && !pathname.startsWith('/wallet') && !pathname.startsWith('/earnings');
  // Not while the profile step is open: a brand-new account has nothing to
  // read yet, and the browser's identity is published under the account.
  const owesProfile = !!session?.onboarding && (session.onboarding.name || session.onboarding.dob);

  // No chat on the web: take this browser's old chat keys out of the
  // directory once, so nobody keeps encrypting for it.
  useEffect(() => {
    if (!session || WEB_CHAT) return;
    void retireWebChatKeys();
  }, [session]);

  useEffect(() => {
    if (!session || !needsCrypto || owesProfile) return;
    // Register the history-paging receiver so pages the phone pushes are
    // routed to the requester.
    installHistoryPaging();
    initCrypto().catch((err) => {
      console.warn('[nsibi] crypto could not start', err);
    });
  }, [session, needsCrypto, owesProfile]);

  if (!session) {
    // One route survives being signed out: a card checkout carrying a
    // deposit token. The phone opens it in an in-app browser that has no
    // cookie and no bearer, and bouncing somebody to /signin to pay for
    // something they already authenticated for in the app is how a
    // payment gets abandoned.
    //
    // Gated on the token actually being there, so a bare
    // /wallet/checkout/<id> typed by a signed-out person still lands on
    // sign-in rather than on a page that can only fail. The token
    // authorises that one deposit and nothing else - see
    // wallet/checkout_token.py.
    const hasCheckoutToken = new URLSearchParams(window.location.search).has('t');
    return (
      <Routes>
        <Route path="/signin" element={<SignIn onSignedIn={onSignedIn} />} />
        {hasCheckoutToken ? (
          <Route
            path="/wallet/checkout/:depositId"
            element={
              <Suspense fallback={<RouteLoading />}>
                <Checkout />
              </Suspense>
            }
          />
        ) : null}
        {/* Somebody sent to top up who is not signed in here goes to the
            coin shop on nsibi.app, which needs no sign-in for mobile money.
            The exception is the shop's own card hand-off
            (`/wallet?pay=card&...`): card is paid signed in, so that one
            signs in first and comes back to open the card checkout. */}
        <Route path="/wallet" element={<SignedOutWallet />} />
        <Route path="/coins" element={<ToCoinShop />} />
        <Route path="/recharge" element={<ToCoinShop />} />
        {/* A video somebody wanted to like or comment on - the public
            site's "Log in to comment" - comes back to that video. */}
        <Route path="/feed/*" element={<SignInFirst />} />
        <Route path="/live/*" element={<SignInFirst />} />
        <Route path="/u/*" element={<SignInFirst />} />
        <Route path="/me" element={<SignInFirst />} />
        <Route path="*" element={<Navigate to="/signin" replace />} />
      </Routes>
    );
  }

  if (owesProfile) {
    return <ProfileSetup session={session} onDone={onSignedIn} onRefused={onProfileRefused} />;
  }

  const home = WEB_CHAT ? '/chats' : '/feed';
  const chats = WEB_CHAT ? (
    <Suspense fallback={<RouteLoading />}>
      <Chats session={session} onSignedOut={onSignedOut} />
    </Suspense>
  ) : (
    <Navigate to="/feed" replace />
  );

  return (
    <SignOutContext.Provider value={onSignedOut}>
    <Routes>
      <Route path="/chats" element={chats} />
      <Route path="/chats/:channelId" element={chats} />
      {/* The timeline. Plaintext by design - E2EE scope is chat only -
          which is why this route needs none of the crypto the chat
          route does. */}
      <Route
        path="/feed"
        element={
          <Suspense fallback={<RouteLoading />}>
            <Feed session={session} />
          </Suspense>
        }
      />
      {/* One live - the same stage as nsibi.app/live/<id>. */}
      <Route
        path="/live/:liveId"
        element={
          <Suspense fallback={<RouteLoading />}>
            <LiveRoom session={session} />
          </Suspense>
        }
      />
      {/* Your own profile, wherever your handle is (the rail's avatar). */}
      <Route
        path="/me"
        element={
          <Suspense fallback={<RouteLoading />}>
            <Me />
          </Suspense>
        }
      />
      {/* One profile - the same page as nsibi.app/u/<handle>, with Follow. */}
      <Route
        path="/u/:handle"
        element={
          <Suspense fallback={<RouteLoading />}>
            <Profile session={session} />
          </Suspense>
        }
      />
      {/* One video, the deck - the same player as nsibi.app/p/<id>. */}
      <Route
        path="/feed/:postId"
        element={
          <Suspense fallback={<RouteLoading />}>
            <FeedWatch session={session} />
          </Suspense>
        }
      />
      {/* The wallet is the shop for every iPhone owner, because coins
          cannot be sold inside the iOS app. It is reached by typing a URL,
          so both paths are top level and memorable. */}
      <Route
        path="/wallet"
        element={
          <Suspense fallback={<RouteLoading />}>
            <Wallet session={session} />
          </Suspense>
        }
      />
      {/* One deposit, one URL - so a refresh, a reopened tab or a 3D
          Secure return all land somewhere that can rebuild itself. */}
      <Route
        path="/wallet/checkout/:depositId"
        element={
          <Suspense fallback={<RouteLoading />}>
            <Checkout />
          </Suspense>
        }
      />
      <Route path="/coins" element={<Navigate to="/wallet" replace />} />
      <Route path="/recharge" element={<Navigate to="/wallet" replace />} />
      <Route
        path="/earnings"
        element={
          <Suspense fallback={<RouteLoading />}>
            <Earnings session={session} />
          </Suspense>
        }
      />
      <Route path="*" element={<Navigate to={home} replace />} />
    </Routes>
    </SignOutContext.Provider>
  );
}

/** Signed out on /wallet: the coin shop, or sign-in for a card purchase. */
function SignedOutWallet() {
  const { pathname, search } = useLocation();
  if (cardPurchaseFrom(search)) {
    return <Navigate to={signInReturningTo(pathname + search)} replace />;
  }
  return <ToCoinShop />;
}

/** Signed out on a page that needs the account: sign in, then back here. */
function SignInFirst() {
  const { pathname, search } = useLocation();
  return <Navigate to={signInReturningTo(pathname + search)} replace />;
}

/** A full navigation: the shop is another site. */
function ToCoinShop() {
  useEffect(() => {
    window.location.replace(coinShopUrl());
  }, []);
  return <RouteLoading />;
}

/** Deliberately plain: this is visible for one network round trip, and a
 *  spinner that animates in and out is more distracting than a line of text. */
function RouteLoading() {
  const { t } = useTranslation();
  return <div className="p-10 text-sm text-muted-foreground">{t("app.loading")}</div>;
}
